import cx from 'classnames';
import { forwardRef, useEffect, useRef } from 'react';

import { SecurityCodeInputHandle, useSecurityCodeInput } from './useSecurityCodeInput';
import { SECURITY_CODE_LENGTH } from '../securityCodeConstants';

export type { SecurityCodeInputHandle } from './useSecurityCodeInput';

type SecurityCodeInputProps = {
  value: string;
  onChange: (value: string) => void;
  error?: string | null;
  helperText?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  onSubmit?: () => void;
  isShaking?: boolean;
  onShakeEnd?: () => void;
};

const SecurityCodeInput = forwardRef<SecurityCodeInputHandle, SecurityCodeInputProps>(
  ({ value, onChange, error, helperText, autoFocus, disabled, onSubmit, isShaking, onShakeEnd }, ref) => {
    const { inputRefs, digits, handleChange, handleKeyDown, handleFocus, handlePaste } = useSecurityCodeInput(
      { value, onChange, autoFocus, onSubmit },
      ref
    );

    const prevTrimmedLenRef = useRef<number | null>(null);
    useEffect(() => {
      const len = value.trim().length;
      const prev = prevTrimmedLenRef.current;
      prevTrimmedLenRef.current = len;

      if (prev !== null && prev === SECURITY_CODE_LENGTH && len === 0) {
        inputRefs.current[0]?.focus();
      }
    }, [value, inputRefs]);

    const trimmed = value.trim();
    const isPartialEntry = trimmed.length > 0 && trimmed.length < SECURITY_CODE_LENGTH;
    const showError = Boolean(error && !isPartialEntry);

    const message = (showError ? error : null) ?? helperText ?? null;
    const messageColorClass = showError ? 'text-alert-50' : 'text-gray-60';

    return (
      <div className="flex flex-col items-center gap-2">
        <div className={cx('flex gap-2', isShaking && 'animate-shake')} onAnimationEnd={onShakeEnd}>
          {digits.map((digit, index) => {
            const hasDigit = digit.trim().length > 0;
            return (
              <input
                key={index}
                ref={(el) => {
                  inputRefs.current[index] = el;
                }}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit.trim() ? '●' : ''}
                onChange={(e) => handleChange(index, e)}
                onFocus={() => handleFocus(index)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
                disabled={disabled}
                className={cx(
                  'h-[55px] w-[47px] shrink-0 rounded-lg border bg-white text-center text-xl font-semibold outline-none transition-colors',
                  'focus:border-primary-50 focus:ring-1 focus:ring-primary-50',
                  showError && hasDigit ? 'border-alert-50' : 'border-gray-40'
                )}
              />
            );
          })}
        </div>
        {message ? <p className={cx('text-center text-sm', messageColorClass)}>{message}</p> : null}
      </div>
    );
  }
);

SecurityCodeInput.displayName = 'SecurityCodeInput';

export default SecurityCodeInput;
